import { useCallback } from 'react'
import { type NavigateOptions, type To, useNavigate } from 'router-v6'
import { toEmbedRoutePath } from 'v2/shared/utils/embed-session'

/**
 * Embed-safe navigate: applies toEmbedRoutePath to every string/pathname
 * destination (identity outside /embed, idempotent), so call sites pass BARE
 * console paths and can never re-introduce the "bare navigation escapes the
 * iframe to the console login" bug (AGENTS.md failure mode 18).
 *
 * Use this instead of router-v6's useNavigate anywhere reachable from an
 * embed surface (agents list, builder, KB, runs) — enforced by
 * __tests__/embed-nav-ban.test.ts. Delta navigation (navigate(-1)) passes
 * through untouched.
 */
/**
 * Apply toEmbedRoutePath to a router destination (string or Path object).
 * ONLY absolute destinations are prefixed: relative ones ('.', '..',
 * 'sibling') resolve against the CURRENT location — already under /embed in
 * embed mode — and prefixing would corrupt them ('.' → '/embed.').
 */
export const toEmbedTo = (to: To): To => {
  if (typeof to === 'string') {
    return to.startsWith('/') ? toEmbedRoutePath(to) : to
  }
  return to.pathname?.startsWith('/') ? { ...to, pathname: toEmbedRoutePath(to.pathname) } : to
}

export const useAppNavigate = () => {
  const navigate = useNavigate()
  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to)
        return
      }
      navigate(toEmbedTo(to), options)
    },
    [navigate],
  )
}
